import {
  hashKey,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { trpc } from "@client/trpc";
import {
  artifactProcedureFromQueryKey,
  shouldEvictApplicationQuery,
} from "./artifactQueryPersistence";
import { evictArtifactQueries } from "./queryClientPersister";

function unavailableState(error?: unknown) {
  const unavailable =
    error instanceof Error
      ? error
      : new Error("This submitted application is no longer available.");
  return {
    data: undefined,
    error: unavailable,
    fetchFailureReason: unavailable,
    status: "error" as const,
    fetchStatus: "idle" as const,
  };
}

/** A denied source invalidates both its text and the round's derived search text. */
export async function revokeApplicationCache(
  client: QueryClient,
  queryKey: QueryKey,
  error: unknown,
  { evictPersistedArtifacts: persisted }: { evictPersistedArtifacts: boolean },
): Promise<void> {
  if (!shouldEvictApplicationQuery(queryKey, error)) {
    return;
  }

  client
    .getQueryCache()
    .find({ queryKey, exact: true })
    ?.setState(unavailableState(error));
  const input = (queryKey[1] as { input?: { roundId?: unknown } } | undefined)
    ?.input;
  const searchKey =
    typeof input?.roundId === "string"
      ? getQueryKey(
          trpc.getProposalApplicationSearch,
          { roundId: input.roundId },
          "query",
        )
      : null;

  if (searchKey && hashKey(searchKey) === hashKey(queryKey)) {
    // A round-wide denial also revokes all individual source projections.
    const matches = (query: { queryKey: QueryKey }) => {
      const procedure = artifactProcedureFromQueryKey(query.queryKey);
      const candidate = (
        query.queryKey[1] as { input?: { roundId?: unknown } } | undefined
      )?.input;
      return (
        candidate?.roundId === input?.roundId &&
        (procedure === "getProposalApplication" ||
          procedure === "getProposalApplicationSearch")
      );
    };

    await client.cancelQueries({ predicate: matches });

    for (const query of client
      .getQueryCache()
      .findAll({ predicate: matches })) {
      query.setState(unavailableState(error));
    }

    if (persisted) {
      await evictArtifactQueries(matches);
    }

    return;
  }

  if (searchKey && hashKey(searchKey) !== hashKey(queryKey)) {
    // Cancel first: a search started before the denial must not restore old text.
    await client.cancelQueries({ queryKey: searchKey, exact: true });
    client
      .getQueryCache()
      .find({ queryKey: searchKey, exact: true })
      ?.setState(unavailableState(error));
  }

  if (persisted) {
    const hashes = new Set([
      hashKey(queryKey),
      ...(searchKey ? [hashKey(searchKey)] : []),
    ]);
    await evictArtifactQueries((query) => hashes.has(query.queryHash));
  }

  if (searchKey && hashKey(searchKey) !== hashKey(queryKey)) {
    // Rebuild mounted search immediately. Recovery must not block round opening
    // on a slow/offline request; inactive search refetches on its next mount.
    void client.invalidateQueries({ queryKey: searchKey, exact: true });
  }
}

/** Successful search can also authoritatively report individual sources gone. */
export async function reconcileApplicationSearchCache(
  client: QueryClient,
  queryKey: QueryKey,
  data: unknown,
  persisted: boolean,
): Promise<void> {
  if (
    artifactProcedureFromQueryKey(queryKey) !==
      "getProposalApplicationSearch" ||
    !data ||
    typeof data !== "object"
  ) {
    return;
  }

  const unavailable = new Set(
    Object.entries(data)
      .filter(([, answers]) => answers === null)
      .map(([id]) => id),
  );

  if (!unavailable.size) {
    return;
  }

  const roundId = (queryKey[1] as { input?: { roundId?: unknown } } | undefined)
    ?.input?.roundId;

  if (typeof roundId !== "string") {
    return;
  }

  const matches = (query: { queryKey: QueryKey }) => {
    const input = (
      query.queryKey[1] as
        | { input?: { roundId?: unknown; proposalId?: unknown } }
        | undefined
    )?.input;
    return (
      artifactProcedureFromQueryKey(query.queryKey) ===
        "getProposalApplication" &&
      input?.roundId === roundId &&
      typeof input.proposalId === "string" &&
      unavailable.has(input.proposalId)
    );
  };

  await client.cancelQueries({ predicate: matches });

  for (const query of client.getQueryCache().findAll({ predicate: matches })) {
    query.setState(unavailableState());
  }

  if (persisted) {
    await evictArtifactQueries(matches);
  }
}
